// src/api/timeToCash.js
import { apiGet } from "../client";

/**
 * Clamp negative "days" to 0 for display, but preserve raw values for tooltips/debug.
 *
 * Accepts rows shaped like:
 *  - { stage: "Application", days: -12 }
 *  - { label: "Approval", value: -5 }
 *  - { name: "Valuation", days: 10 }
 */
export function normalizeTimeToCashStages(rows = []) {
  let anomalyCount = 0;

  const data = (Array.isArray(rows) ? rows : []).map((r) => {
    // Try common numeric keys used by APIs/charts
    const raw = Number(
      r?.days ?? r?.value ?? r?.duration ?? r?.avg_days ?? r?.avg ?? 0
    );

    const isFiniteNum = Number.isFinite(raw);
    const isAnomaly = isFiniteNum && raw < 0;

    if (isAnomaly) anomalyCount += 1;

    return {
      ...r,

      // Keep the raw backend number for tooltip/debug
      rawDays: isFiniteNum ? raw : 0,

      // ✅ Chart-friendly "days" value
      // If backend gives negative, show 0 instead (Option A)
      days: !isFiniteNum ? 0 : isAnomaly ? 0 : raw,

      isAnomaly,
      note: isAnomaly
        ? "Backend returned negative days (data anomaly). Displayed as 0."
        : "",
    };
  });

  return { data, anomalyCount };
}

/**
 * Optional: normalize a whole API payload if your backend returns nested shapes.
 * This safely attempts to find the stages array and normalize it.
 */
export function normalizeTimeToCashResponse(payload = {}) {
  // Common places arrays might live (adjust as per your real response)
  const stages =
    payload?.stages ||
    payload?.data ||
    payload?.results ||
    payload?.message?.stages ||
    payload?.message?.data ||
    payload?.message?.results;

  const { data, anomalyCount } = normalizeTimeToCashStages(
    Array.isArray(stages) ? stages : []
  );

  return {
    ...payload,
    stages: data, // expose a consistent key for the frontend if you want
    anomalyCount,
  };
}

/**
 * Call dynesourcerapi.api.get_time_to_cash_per_stage_per_lender
 *
 * Example endpoints:
 *  - /api/method/dynesourcerapi.api.get_time_to_cash_per_stage_per_lender
 *  - /api/method/dynesourcerapi.api.get_time_to_cash_per_stage_per_lender?months=1
 *  - /api/method/dynesourcerapi.api.get_time_to_cash_per_stage_per_lender?months=3
 *  - /api/method/dynesourcerapi.api.get_time_to_cash_per_stage_per_lender?months=6
 *
 * @param {Object} opts
 * @param {number|string} [opts.months]  - 1, 3 or 6 (optional)
 * @param {string}        [opts.purpose] - purchase, remortgage, btl, etc. (optional)
 *
 * @returns {Promise<Object>} Normalised payload (usually the `message` object)
 */
export async function getTimeToCashPerStagePerLender(opts = {}) {
  const { months, purpose } = opts || {};
  const params = {};

  // Only send months if it is a valid number (1, 3, 6)
  if (
    months !== undefined &&
    months !== null &&
    String(months).trim() !== "" &&
    !Number.isNaN(Number(months))
  ) {
    params.months = Number(months);
  }

  // Purpose is optional; backend may ignore it, but sending is harmless
  if (purpose) {
    params.purpose = String(purpose).toLowerCase();
  }

  // Use shared axios instance so token/cookies are attached automatically
  const data = await apiGet(
    "/api/method/dynesourcerapi.api.get_time_to_cash_per_stage_per_lender",
    params
  );

  // Frappe usually returns { message: ... }
  const payload =
    data && typeof data === "object" && "message" in data ? data.message : data;

  return payload || {};
}

/**
 * Invoice Finance: dynesourcerapi.if_analytics.get_if_time_to_cash_per_stage
 * Returns { stages: [names], avg_days_per_stage: { Enquiry, Application, Approval, Audit, Legals, Completion }, ... }
 * (no purpose split, no per-stage counts).
 *
 * @param {{ months?: number }} opts
 */
export async function getIfTimeToCashPerStage(opts = {}) {
  const months = Number(opts?.months);
  const params = Number.isFinite(months) && months > 0 ? { months } : {};

  const data = await apiGet(
    "/api/method/dynesourcerapi.if_analytics.get_if_time_to_cash_per_stage",
    params
  );

  const payload =
    data && typeof data === "object" && "message" in data ? data.message : data;

  return payload || {};
}

const RANGE_KEYS_BY_MONTHS = {
  1: ["1m", "1", "30d", "last_30_days", "1_month", "month_1", "months_1"],
  3: ["3m", "3", "90d", "last_3_months", "3_months", "month_3", "months_3"],
  6: ["6m", "6", "180d", "last_6_months", "6_months", "month_6", "months_6"],
};

/**
 * Invoice Finance: dynesourcerapi.if_analytics.get_if_time_to_cash_per_stage_ranges
 * Returns every range block in one call, keyed by range (e.g. { "1m": {...}, "3m": {...}, "6m": {...} }).
 */
export async function getIfTimeToCashPerStageRanges() {
  const data = await apiGet(
    "/api/method/dynesourcerapi.if_analytics.get_if_time_to_cash_per_stage_ranges"
  );
  const payload =
    data && typeof data === "object" && "message" in data ? data.message : data;
  return payload || {};
}

/**
 * Pick the block for 1 / 3 / 6 months out of a get_if_time_to_cash_per_stage_ranges payload.
 * Returns null when the payload has no block for that range.
 */
export function pickIfTimeToCashRange(rangesPayload, months) {
  if (!rangesPayload || typeof rangesPayload !== "object") return null;
  const source =
    rangesPayload.ranges && typeof rangesPayload.ranges === "object"
      ? rangesPayload.ranges
      : rangesPayload;

  if (Array.isArray(source)) {
    const match = source.find(
      (row) => Number(row?.months ?? row?.range_months) === Number(months) ||
        (RANGE_KEYS_BY_MONTHS[months] || []).includes(String(row?.range ?? row?.key ?? "").toLowerCase())
    );
    return match || null;
  }

  const lowerKeyMap = Object.fromEntries(
    Object.keys(source).map((k) => [k.toLowerCase(), k])
  );
  for (const key of RANGE_KEYS_BY_MONTHS[months] || []) {
    const original = lowerKeyMap[key];
    if (original && source[original] && typeof source[original] === "object") {
      return source[original];
    }
  }
  return null;
}
